import { GlobalState, UI, UIReducer } from '@leverege/ui-redux';
import { zlibSync, unzlibSync, strToU8, strFromU8 } from 'fflate';
window.strToU8 = strToU8;
window.strFromU8 = strFromU8;
export default class LocalUserSettingsDelegate {
  constructor(opts) {
    this.options = opts;
    this.rootPath = opts?.rootPath || ['userSettings'];
    this.rootLocalPath = [...this.rootPath, 'local'];
    this.rootSharedPath = [...this.rootPath, 'shared'];
    this.localStorageKey = opts?.localStorageKey || 'lev-user-settings';
    this.debounceTime = opts?.debounceTime || 1000;
    this.profile = this.getDefaultProfile(opts?.profile);
    this.fallback = [{
      name: 'local',
      rootPathArgs: [true]
    }, {
      name: 'shared',
      rootPathArgs: [false]
    }];
    this.state = {};
    window.lev_logLocalUserSettings = () => {
      try {
        const val = localStorage.getItem(this.localStorageKey);
        if (val) {
          const decomp = unzlibSync(strToU8(val, true));
          if (decomp) {
            console.log(JSON.parse(strFromU8(decomp)));
          }
        }
      } catch (err) {
        console.log(err);
      }
    };
  }
  async init(opts) {
    let iVal = null;
    try {
      const val = localStorage.getItem(this.localStorageKey);
      if (val) {
        const decomp = unzlibSync(strToU8(val, true));
        if (decomp) {
          iVal = JSON.parse(strFromU8(decomp));
          GlobalState.dispatch(UI.set(this.getRootPath(true), iVal));
        }
      }
    } catch (err) {
      console.warn('loading user settings failed', err);
    }
    if (opts?.sharedValues) {
      GlobalState.dispatch(UI.set(this.getRootPath(false), opts.sharedValues));
    }
    this.attachStore(opts?.store);
  }
  getRsrcPath(path, fallbackEntry, profile = this.profile) {
    const basePath = this.getRootPath(...fallbackEntry.rootPathArgs);
    return [...basePath, 'values', profile, path];
    // `${basePath}${profile ? `/${profile}` : ''}${path ? `/${path}` : ''}`
  }
  getVersionPath(path, fallbackEntry, version = null) {
    const basePath = this.getRootPath(...fallbackEntry.rootPathArgs);
    return [...basePath, 'versions', path, version];
    // `${basePath}/version${path ? `/${path}` : ''}${version ? `/${version}` : ''}`
  }
  getVersionsPath(path, fallbackEntry, version = null) {
    const basePath = this.getRootPath(...fallbackEntry.rootPathArgs);
    return [...basePath, 'versions', path];
  }
  getDefaultProfile(profile) {
    const p = typeof profile === 'string' ? profile : 'default';
    if (p.endsWith('/')) {
      return p.slice(0, -1);
    }
    return p;
  }
  writeToLocalStorage = () => {
    const val = this.toWrite;
    try {
      const toComp = strToU8(JSON.stringify(val));
      const comp = zlibSync(toComp);
      localStorage.setItem(this.localStorageKey, strFromU8(comp, true));
    } catch (err) {
      console.warn('saving user settings failed', err);
    }
    this.timer = null;
  };
  debounceWrite(val) {
    this.toWrite = val;
    if (!this.timer) {
      this.timer = setTimeout(this.writeToLocalStorage, this.debounceTime);
    }
  }
  async attachStore(store) {
    if (store) {
      store.subscribe(() => {
        const val = UIReducer.get(store.getState(), this.getRootPath(true));
        this.debounceWrite(val);
      });
    }
  }
  getRsrcFromFallback(state, path, fb, profile, opts) {
    const nState = this.getState(state);
    return fb.map(fbEntry => {
      return UIReducer.get(nState, this.getRsrcPath(path, fbEntry, profile ?? this.profile));
    }).find(i => !!i);
  }
  getScope(state, path, opts) {
    return 'local';
  }
  getProfileScope(state, profile, path, opts) {
    return 'local';
  }
  getState(state) {
    return state ?? GlobalState.get();
  }
  getRootPath(local = true) {
    return local ? this.rootLocalPath : this.rootSharedPath;
  }

  // interface functions for current
  get(state, path, opts) {
    return this.getRsrcFromFallback(state, path, this.fallback, null, opts);
  }
  getProfile(state, profile, path, opts) {
    return this.getRsrcFromFallback(state, path, this.fallback, profile, opts);
  }
  set(path, value) {
    return GlobalState.dispatch(UI.set(this.getRsrcPath(path, this.fallback[0]), value));
  }
  reset(path) {
    return GlobalState.dispatch(UI.set(this.getRsrcPath(path, this.fallback[0]), undefined));
  }

  // current version stuff
  getVersion(state, path, version, opts) {
    const nState = this.getState(state);
    return UIReducer.get(nState, this.getVersionPath(path, this.fallback[0], version));
  }
  getVersions(state, path, opts) {
    const nState = this.getState(state);
    const v = UIReducer.get(nState, this.getVersionsPath(path, this.fallback[0]));
    const items = Object.entries(v ?? {});
    return {
      items: items.map(([path, value]) => ({
        path,
        value
      })),
      count: items.length,
      total: items.length
    };
  }
  setVersion(path, version, value, opts) {
    return GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[0], version), value));
  }
  renameVersion(path, oldVersionName, newVersionName, opts) {
    const nState = this.getState(GlobalState.get());
    const v = UIReducer.get(nState, this.getVersionPath(path, this.fallback[0], oldVersionName));
    GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[0], oldVersionName), undefined));
    GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[0], newVersionName), v));
  }
  resetVersion(path, version) {
    return GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[0], version), undefined));
  }
  // end current version stuff
  // end current

  // interface functions for setting a specific shared profile
  getSharedProfile(state, profile, path, opts) {
    this.getRsrcFromFallback(state, path, [this.fallback[1]], profile, opts);
  }
  setSharedProfile(profile, path, value, opts) {
    return GlobalState.dispatch(UI.set(this.getRsrcPath(path, this.fallback[1], profile), value));
  }
  getSharedProfiles(state, profile) {
    return null;
  }
  resetSharedProfile(profile, path, opts) {
    return GlobalState.dispatch(UI.set(this.getRsrcPath(path, this.fallback[1], profile), undefined));
  }
  // end interface functions for a specific profile

  // shared version stuff

  getSharedVersion(state, path, version, opts) {
    const nState = this.getState(state);
    return UIReducer.get(nState, this.getVersionPath(path, this.fallback[1], version));
  }
  getSharedVersions(state, path, opts) {
    const nState = this.getState(state);
    const v = UIReducer.get(nState, this.getVersionsPath(path, this.fallback[1]));
    const items = Object.entries(v ?? {});
    return {
      items: items.map(([path, value]) => ({
        path,
        value
      })),
      count: items.length,
      total: items.length
    };
  }
  setSharedVersion(path, version, value, opts) {
    return GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[1], version), value));
  }
  renameSharedVersion(path, oldVersionName, newVersionName, opts) {
    const nState = this.getState(GlobalState.get());
    const v = UIReducer.get(nState, this.getVersionPath(path, this.fallback[1], oldVersionName));
    GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[1], oldVersionName), undefined));
    GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[1], newVersionName), v));
  }
  resetSharedVersion(path, version) {
    return GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[1], version), undefined));
  }
  // end shared version stuff

  // interface functions for setting a user's specific profile
  getUserProfile(state, profile, path, opts) {
    this.getRsrcFromFallback(state, path, [this.fallback[0]], profile, opts);
  }
  setUserProfile(profile, path, value, opts) {
    return GlobalState.dispatch(UI.set(this.getRsrcPath(path, this.fallback[0], profile), value));
  }
  getUserProfiles(state, profile) {
    return null;
  }
  resetUserProfile(profile, path, opts) {
    return GlobalState.dispatch(UI.set(this.getRsrcPath(path, this.fallback[0], profile), undefined));
  }
  // end interface functions for a user's specific profile

  // user version stuff
  getUserVersion(state, path, version, opts) {
    const nState = this.getState(state);
    return UIReducer.get(nState, this.getVersionPath(path, this.fallback[0], version));
  }
  getUserVersions(state, path, opts) {
    const nState = this.getState(state);
    const v = UIReducer.get(nState, this.getVersionsPath(path, this.fallback[0]));
    const items = Object.entries(v ?? {});
    return {
      items: items.map(([path, value]) => ({
        path,
        value
      })),
      count: items.length,
      total: items.length
    };
  }
  setUserVersion(path, version, value, opts) {
    return GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[0], version), value));
  }
  renameVersion(path, oldVersionName, newVersionName, opts) {
    const nState = this.getState(GlobalState.get());
    const v = UIReducer.get(nState, this.getVersionPath(path, this.fallback[0], oldVersionName));
    GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[0], oldVersionName), undefined));
    GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[0], newVersionName), v));
  }
  resetUserVersion(path, version) {
    return GlobalState.dispatch(UI.set(this.getVersionPath(path, this.fallback[0], version), undefined));
  }
  // end user version stuff
}